import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Form, Input, Label, Alert, Spinner, Button,
} from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import api from '../../helpers/api';
import { uploadServiceContractFiles, validateFiles } from '../../helpers/fileUpload';

/**
 * Lightweight upload form for the Knowledge Hub. Captures the four things the user's
 * spec called for — Name, Module, Entity (depends on Module), File — and writes a
 * row into the new {@code documents} table via POST /documents/create.
 *
 * <p>Modules with confirmed list endpoints are wired here; aggregation continues to
 * cover every other module's own attachment surfaces regardless of what's exposed in
 * this dropdown.</p>
 */

const ENTITY_SOURCES = {
  HUB: null,
  CERTIFICATE: {
    endpoint: 'certificates/get-all',
    extractList: (data) => data?.response || [],
    optionLabel: (c) => c.name || `Certificate #${c.id}`,
    searchKey: 'searchTerm',
  },
  CONTRACT: {
    endpoint: 'contract/get-all',
    extractList: (data) => data?.response?.content || data?.response || [],
    optionLabel: (c) => c.contractNumber || `Contract #${c.id}`,
    searchKey: 'searchTerm',
  },
  ASSET: {
    endpoint: 'asset/all',
    // The /asset/all response shape varies: data.response may be an array whose
    // first element has an `assets` sub-array, OR an object with .assets / .content.
    // Mirror the unwrap used by AssetMultiPicker so option ids resolve correctly.
    extractList: (data) => {
      const raw = data?.response;
      if (Array.isArray(raw)) return raw[0]?.assets || raw;
      return raw?.assets || raw?.content || [];
    },
    optionLabel: (a) => {
      const name = a.assetName || a.name || `Asset #${a.id}`;
      const suffix = a.assetNumber ? ` · ${a.assetNumber}` : '';
      return `${name}${suffix}`;
    },
    // Backend list endpoint expects ?search=… (not searchTerm).
    searchKey: 'search',
  },
  VENDOR: {
    endpoint: 'vendor/getAllVendors',
    extractList: (data) => data?.response || [],
    optionLabel: (v) => v.vendorName || `Vendor #${v.id}`,
    searchKey: null,
  },
  WORK_ORDER: {
    endpoint: 'asset-issues/organization/filtered',
    extractList: (data) => data?.response?.content || data?.response || [],
    optionLabel: (i) => i.title || i.ticketNumber || `Work Order #${i.id}`,
    searchKey: 'searchTerm',
  },
};

const emptyForm = () => ({
  name: '',
  sourceModule: 'HUB',
  sourceEntityId: '',
  documentUrl: '',
  fileName: '',
  notes: '',
});

// Matches the canonical react-select look used in Assets.jsx (blue options,
// Bootstrap-style focus/error borders).
const selectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    borderColor: hasError ? '#dc3545' : (state.isFocused ? '#86b7fe' : '#ced4da'),
    boxShadow: hasError
      ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
      : (state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none'),
    '&:hover': { borderColor: '#86b7fe' },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected || state.isFocused ? '#3980c0' : 'white',
    color: state.isSelected || state.isFocused ? 'white' : '#212529',
    '&:active': { backgroundColor: '#2e669a' },
  }),
});

const AddDocumentModal = ({ isOpen, onClose, onCreated }) => {
  const { t } = useTranslation();

  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [apiError, setApiError] = useState('');

  const [entityOptions, setEntityOptions] = useState([]);
  const [entityLoading, setEntityLoading] = useState(false);
  const [entitySearch, setEntitySearch] = useState('');
  // Holds the full selected option ({value,label}) so the chosen item keeps
  // showing in the combobox even after the options list is refetched on search.
  const [selectedEntity, setSelectedEntity] = useState(null);
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setForm(emptyForm());
      setErrors({});
      setApiError('');
      setEntityOptions([]);
      setEntitySearch('');
      setSelectedEntity(null);
    }
  }, [isOpen]);

  const moduleConfig = ENTITY_SOURCES[form.sourceModule];

  const loadEntities = useCallback(async (cfg, searchTerm) => {
    if (!cfg) { setEntityOptions([]); return; }
    setEntityLoading(true);
    try {
      const params = {};
      if (cfg.searchKey && searchTerm) params[cfg.searchKey] = searchTerm;
      const { data } = await api.get(cfg.endpoint, { params });
      setEntityOptions(cfg.extractList(data));
    } catch (err) {
      console.error('Error loading entities for', form.sourceModule, err);
      setEntityOptions([]);
    } finally {
      setEntityLoading(false);
    }
  }, [form.sourceModule]);

  useEffect(() => {
    if (!isOpen) return;
    if (!moduleConfig) { setEntityOptions([]); return; }
    loadEntities(moduleConfig, entitySearch);
  }, [isOpen, moduleConfig, entitySearch, loadEntities]);

  const entityChoices = useMemo(
    () => (moduleConfig
      ? entityOptions.map((opt) => ({ value: opt.id, label: moduleConfig.optionLabel(opt) }))
      : []),
    [entityOptions, moduleConfig],
  );

  const handleEntityInput = (input, meta) => {
    // Only react to actual typing — ignore blur/menu-close so the list isn't cleared.
    if (meta && meta.action !== 'input-change') return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setEntitySearch(input), 300);
  };

  const handleFieldChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    if (field === 'sourceModule') {
      setForm((prev) => ({ ...prev, sourceEntityId: '' }));
      setEntitySearch('');
      setSelectedEntity(null);
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    const validation = validateFiles(files);
    if (!validation.valid) {
      setErrors((prev) => ({ ...prev, file: validation.errors?.[0] || t('knowledge_hub.add_document.error.upload_failed', 'Upload failed.') }));
      return;
    }
    setUploading(true);
    setErrors((prev) => ({ ...prev, file: undefined }));
    try {
      const uploaded = await uploadServiceContractFiles(files);
      const url = (uploaded || []).filter(Boolean)[0];
      if (!url) {
        setErrors((prev) => ({ ...prev, file: t('knowledge_hub.add_document.error.upload_failed', 'Upload failed.') }));
      } else {
        setForm((prev) => ({ ...prev, documentUrl: url, fileName: files[0].name }));
      }
    } catch (err) {
      console.error('Upload error', err);
      setErrors((prev) => ({ ...prev, file: t('knowledge_hub.add_document.error.upload_failed', 'Upload failed.') }));
    } finally {
      setUploading(false);
    }
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = t('knowledge_hub.add_document.error.name_required', 'Document name is required.');
    if (!form.sourceModule) next.sourceModule = t('knowledge_hub.add_document.error.module_required', 'Module is required.');
    if (form.sourceModule !== 'HUB' && !form.sourceEntityId) {
      next.sourceEntityId = t('knowledge_hub.add_document.error.entity_required', 'Please select an item.');
    }
    if (!form.documentUrl) next.file = t('knowledge_hub.add_document.error.file_required', 'Please attach a file.');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;
    setSubmitting(true);
    setApiError('');
    try {
      const payload = {
        name: form.name.trim(),
        documentUrls: [form.documentUrl],
        sourceModule: form.sourceModule,
        sourceEntityId: form.sourceModule === 'HUB' ? null : Number(form.sourceEntityId),
        notes: form.notes?.trim() || null,
      };
      const { data } = await api.post('documents/create', payload);
      // Await the parent's onCreated handler so the list refetch completes before the
      // modal closes. Without the await, the modal closed before the new doc landed in
      // the parent's state — user had to reload to see it.
      if (onCreated) await onCreated(data?.response);
      onClose?.();
    } catch (err) {
      console.error('Create document error', err);
      setApiError(err?.response?.data?.message
        || t('knowledge_hub.add_document.error.create_failed', 'Could not create the document. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const moduleOptions = useMemo(() => ([
    { value: 'HUB',         label: t('knowledge_hub.modules.hub',         'Hub upload (unlinked)') },
    { value: 'CERTIFICATE', label: t('knowledge_hub.modules.certificate', 'Certificate') },
    { value: 'CONTRACT',    label: t('knowledge_hub.modules.contract',    'Contract') },
    { value: 'ASSET',       label: t('knowledge_hub.modules.asset',       'Asset') },
    { value: 'VENDOR',      label: t('knowledge_hub.modules.vendor',      'Vendor') },
    { value: 'WORK_ORDER',  label: t('knowledge_hub.modules.work_order',  'Work Order') },
  ]), [t]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" backdrop="static">
      <ModalHeader toggle={onClose}>
        {t('knowledge_hub.add_document.title', 'Add document')}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {apiError && <Alert color="danger">{apiError}</Alert>}

          <div className="mb-3">
            <Label for="kh-doc-name">
              {t('knowledge_hub.add_document.field.name', 'Document name')} <span className="text-danger">*</span>
            </Label>
            <Input
              id="kh-doc-name"
              type="text"
              value={form.name}
              onChange={handleFieldChange('name')}
              invalid={!!errors.name}
              placeholder={t('knowledge_hub.add_document.placeholder.name', 'e.g. Calibration report 2026')}
            />
            {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
          </div>

          <div className="mb-3">
            <Label for="kh-doc-module">
              {t('knowledge_hub.add_document.field.module', 'Module')} <span className="text-danger">*</span>
            </Label>
            <Input
              id="kh-doc-module"
              type="select"
              value={form.sourceModule}
              onChange={handleFieldChange('sourceModule')}
              invalid={!!errors.sourceModule}
            >
              {moduleOptions.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </Input>
            {errors.sourceModule && <div className="invalid-feedback d-block">{errors.sourceModule}</div>}
          </div>

          {form.sourceModule !== 'HUB' && (
            <div className="mb-3">
              <Label for="kh-doc-entity">
                {t('knowledge_hub.add_document.field.entity', 'Select item')} <span className="text-danger">*</span>
              </Label>
              <Select
                inputId="kh-doc-entity"
                classNamePrefix="react-select"
                isClearable
                isLoading={entityLoading}
                options={entityChoices}
                value={selectedEntity}
                onChange={(opt) => {
                  setSelectedEntity(opt || null);
                  setForm((prev) => ({ ...prev, sourceEntityId: opt ? opt.value : '' }));
                  setErrors((prev) => ({ ...prev, sourceEntityId: undefined }));
                }}
                onInputChange={handleEntityInput}
                // Modules with a server-side search key are already filtered by the
                // API, so skip client filtering. Modules without one (e.g. VENDOR)
                // fall back to react-select's built-in label filtering.
                filterOption={moduleConfig?.searchKey ? null : undefined}
                styles={selectStyles(!!errors.sourceEntityId)}
                placeholder={t('knowledge_hub.add_document.placeholder.entity_search', 'Type to search…')}
                loadingMessage={() => t('knowledge_hub.add_document.entity_loading', 'Loading…')}
                noOptionsMessage={() => (entityLoading
                  ? t('knowledge_hub.add_document.entity_loading', 'Loading…')
                  : t('knowledge_hub.add_document.no_results', 'No results found'))}
              />
              {errors.sourceEntityId && (
                <div className="invalid-feedback d-block">{errors.sourceEntityId}</div>
              )}
            </div>
          )}

          <div className="mb-3">
            <Label>
              {t('knowledge_hub.add_document.field.file', 'File')} <span className="text-danger">*</span>
            </Label>
            <div
              className="border rounded p-4 text-center"
              style={{
                borderStyle: 'dashed',
                borderWidth: '2px',
                borderColor: '#dee2e6',
                backgroundColor: '#fafbfc',
              }}
            >
              <input
                id="kh-doc-file-input"
                type="file"
                className="d-none"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,image/*"
                onChange={handleFileSelect}
                disabled={uploading}
              />
              <label
                htmlFor="kh-doc-file-input"
                className={`btn btn-outline-secondary mb-2 ${uploading ? 'disabled' : ''}`}
                style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}
              >
                {uploading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {t('knowledge_hub.add_document.uploading', 'Uploading…')}
                  </>
                ) : (
                  <>
                    <FeatherIcon icon="paperclip" size={16} className="me-2" />
                    {t('shared.attach_files', 'Attach Files')}
                  </>
                )}
              </label>
              <p className="text-muted small mb-0">
                {t('business.document_vault.field.upload_help', 'PDF, Word, Excel, CSV, or image. Max 50 MB per file.')}
              </p>
            </div>
            {form.fileName && !uploading && (
              <div className="mt-2 d-flex align-items-center justify-content-between bg-light rounded px-3 py-2">
                <span className="d-flex align-items-center text-decoration-none" style={{ minWidth: 0, flex: 1 }}>
                  <FeatherIcon icon="file" size={16} className="text-primary me-2" />
                  <span className="text-truncate" style={{ maxWidth: 360 }}>{form.fileName}</span>
                </span>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => setForm((prev) => ({ ...prev, documentUrl: '', fileName: '' }))}
                  disabled={uploading}
                >
                  <FeatherIcon icon="trash-2" size={14} />
                </button>
              </div>
            )}
            {errors.file && <div className="invalid-feedback d-block">{errors.file}</div>}
          </div>

          <div className="mb-2">
            <Label for="kh-doc-notes">
              {t('knowledge_hub.add_document.field.notes', 'Notes')}
            </Label>
            <Input
              id="kh-doc-notes"
              type="textarea"
              rows={3}
              value={form.notes}
              onChange={handleFieldChange('notes')}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" type="button" onClick={onClose} disabled={submitting}>
            {t('shared.cancel', 'Cancel')}
          </Button>
          <Button color="primary" type="submit" disabled={submitting || uploading}>
            {submitting && <Spinner size="sm" className="me-2" />}
            {t('knowledge_hub.add_document.submit', 'Add document')}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddDocumentModal;
